import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { projectService, type ProjectDto } from "@/services/projectService";
import { useAuth } from "@/contexts/AuthContext";

export const ALL_LOCATIONS_ID = "ALL_LOCATIONS";
const STORAGE_KEY = "selected-project-id";

type ProjectSelectionContextValue = {
  projects: ProjectDto[];
  isLoading: boolean;
  error: string | null;
  selectedProjectId: string;
  setSelectedProjectId: (id: string) => void;
  effectiveProjectId?: string;
  resolveProjectId: (defaultProjectId?: string) => string | undefined;
  refreshProjects: () => Promise<void>;
  setProjects: React.Dispatch<React.SetStateAction<ProjectDto[]>>;
};

const ProjectSelectionContext = createContext<ProjectSelectionContextValue | undefined>(undefined);

const normalizeStoredProjectId = (value: string | null) => value || ALL_LOCATIONS_ID;

export const ProjectSelectionProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectIdState] = useState<string>(
    () => normalizeStoredProjectId(localStorage.getItem(STORAGE_KEY))
  );

  const refreshProjects = useCallback(async () => {
    if (!isAuthenticated) {
      setProjects([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const list = await projectService.list();
      setProjects(list);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to load locations";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  useEffect(() => {
    const stored = normalizeStoredProjectId(localStorage.getItem(STORAGE_KEY));
    if (stored !== selectedProjectId) {
      setSelectedProjectIdState(stored);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selectedProjectId);
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId === ALL_LOCATIONS_ID) return;
    if (projects.length === 0) return;
    const exists = projects.some((project) => project.id === selectedProjectId);
    if (!exists) {
      setSelectedProjectIdState(ALL_LOCATIONS_ID);
    }
  }, [projects, selectedProjectId]);

  const setSelectedProjectId = useCallback((id: string) => {
    setSelectedProjectIdState(id || ALL_LOCATIONS_ID);
  }, []);

  const effectiveProjectId = useMemo(
    () => (selectedProjectId === ALL_LOCATIONS_ID ? undefined : selectedProjectId),
    [selectedProjectId]
  );

  const resolveProjectId = useCallback(
    (defaultProjectId?: string) => {
      if (selectedProjectId === ALL_LOCATIONS_ID) {
        return defaultProjectId;
      }
      return selectedProjectId;
    },
    [selectedProjectId]
  );

  const value = useMemo(
    () => ({
      projects,
      setProjects,
      isLoading,
      error,
      selectedProjectId,
      setSelectedProjectId,
      effectiveProjectId,
      resolveProjectId,
      refreshProjects,
    }),
    [
      projects,
      isLoading,
      error,
      selectedProjectId,
      setSelectedProjectId,
      effectiveProjectId,
      resolveProjectId,
      refreshProjects,
    ]
  );

  return <ProjectSelectionContext.Provider value={value}>{children}</ProjectSelectionContext.Provider>;
};

export const useProjectSelection = () => {
  const context = useContext(ProjectSelectionContext);
  if (!context) {
    throw new Error("useProjectSelection must be used within a ProjectSelectionProvider");
  }
  return context;
};
